import { useEffect, useRef, useState } from "react";
import {
	Alert,
	AlertDialog,
	AlertDialogBody,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	AlertIcon,
	Avatar,
	Box,
	Button,
	Divider,
	Flex,
	FormControl,
	FormLabel,
	HStack,
	Heading,
	IconButton,
	Input,
	InputGroup,
	InputRightElement,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spinner,
	Stack,
	Text,
	Tooltip,
	VStack,
	useDisclosure,
	useToast,
} from "@chakra-ui/react";
import { BiHide, BiShow, BiLogOut, BiKey, BiUserPlus, BiUser, BiGroup, BiTrash, BiCopy } from "react-icons/bi";
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";

import { TemplatePage } from "components/layout/template";
import { useLangContext } from "configs/languageContext";
import { useAuthContext } from "configs/authContext";
import { app } from "configs/firebaseConfig";
import { login as login_t, emp_account } from "data/translations";

const C = {
	primary: "#059669",
	primaryLight: "#ECFDF5",
	primaryDark: "#047857",
	ink: "#0F172A",
	muted: "#64748B",
	border: "#E2E8F0",
	bg: "#F8FAFC",
};

// ─────────────────────────────────────────────────────────────────────────
// Reusable section card with our refreshed look.
// ─────────────────────────────────────────────────────────────────────────
const Section = ({
	icon,
	title,
	subtitle,
	children,
}: {
	icon?: React.ReactNode;
	title: string;
	subtitle?: string;
	children: React.ReactNode;
}) => (
	<Box
		bg="white"
		borderWidth="1px"
		borderColor={C.border}
		borderRadius="2xl"
		boxShadow="0 1px 3px rgba(15,23,42,0.04)"
		overflow="hidden"
	>
		<HStack
			spacing={3}
			px={6}
			py={4}
			borderBottomWidth="1px"
			borderColor={C.border}
			bg={C.bg}
		>
			{icon && (
				<Flex w="36px" h="36px" borderRadius="lg" bg={C.primaryLight} color={C.primary} align="center" justify="center" fontSize="lg">
					{icon}
				</Flex>
			)}
			<Box>
				<Heading size="sm" color={C.ink} fontWeight="700">{title}</Heading>
				{subtitle && <Text fontSize="xs" color={C.muted} mt={0.5}>{subtitle}</Text>}
			</Box>
		</HStack>
		<Box p={6}>{children}</Box>
	</Box>
);

const PasswordInput = ({
	value,
	onChange,
	placeholder,
	autoComplete,
}: {
	value: string;
	onChange: (v: string) => void;
	placeholder?: string;
	autoComplete?: string;
}) => {
	const [show, setShow] = useState(false);
	return (
		<InputGroup>
			<Input
				type={show ? "text" : "password"}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder={placeholder}
				autoComplete={autoComplete}
				bg="white"
				borderColor={C.border}
				focusBorderColor={C.primary}
				borderRadius="lg"
			/>
			<InputRightElement>
				<IconButton
					aria-label={show ? "hide" : "show"}
					icon={show ? <BiHide /> : <BiShow />}
					variant="ghost"
					size="sm"
					onClick={() => setShow((s) => !s)}
				/>
			</InputRightElement>
		</InputGroup>
	);
};

const EmployeeAccountPage = () => {
	const { lang } = useLangContext();
	const { currentUser, logout, changePassword } = useAuthContext();
	const toast = useToast();

	const t = (key: string, fallback?: string) =>
		emp_account[key]?.[lang] ?? login_t[key]?.[lang] ?? fallback ?? key;

	// --- Change password ---
	const [curPw, setCurPw] = useState("");
	const [newPw, setNewPw] = useState("");
	const [confirmPw, setConfirmPw] = useState("");
	const [pwBusy, setPwBusy] = useState(false);
	const [pwErr, setPwErr] = useState<string | null>(null);

	const pwTooShort = newPw.length > 0 && newPw.length < 6;
	const pwMismatch = confirmPw.length > 0 && newPw !== confirmPw;
	const pwCanSubmit =
		curPw.length >= 1 && newPw.length >= 6 && newPw === confirmPw && !pwBusy;

	async function doChangePassword(e: React.FormEvent) {
		e.preventDefault();
		setPwErr(null);
		setPwBusy(true);
		try {
			await changePassword(curPw, newPw);
			setCurPw(""); setNewPw(""); setConfirmPw("");
			toast({
				title: t("password_updated"),
				status: "success",
				duration: 4000,
				isClosable: true,
				position: "top",
			});
		} catch (err: any) {
			const code = err?.code || "";
			let msg = t("user_add_failed");
			if (code.includes("wrong-password") || code.includes("invalid-credential")) {
				msg = t("password_wrong_current");
			} else if (code.includes("weak-password")) {
				msg = t("password_weak");
			} else if (code.includes("requires-recent-login")) {
				msg = t("session_too_old");
			} else if (err?.message) {
				msg = err.message;
			}
			setPwErr(msg);
		} finally {
			setPwBusy(false);
		}
	}

	// --- Staff list ---
	interface StaffMember {
		uid: string;
		email: string | null;
		displayName: string | null;
		creationTime: string | null;
		lastSignInTime: string | null;
		disabled: boolean;
	}
	const [staff, setStaff] = useState<StaffMember[] | null>(null);
	const [staffLoading, setStaffLoading] = useState(true);
	const [staffErr, setStaffErr] = useState<string | null>(null);

	async function loadStaff() {
		setStaffLoading(true);
		setStaffErr(null);
		try {
			const functions = getFunctions(app);
			const fn = httpsCallable<unknown, { users: StaffMember[]; total: number }>(functions, "listStaff");
			const res = await fn();
			setStaff(res.data.users);
		} catch (e: any) {
			setStaffErr(e?.message || t("team_error"));
		} finally {
			setStaffLoading(false);
		}
	}

	useEffect(() => {
		if (currentUser) loadStaff();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentUser]);

	// Delete + reset password state
	const [delTarget, setDelTarget] = useState<StaffMember | null>(null);
	const delDisclosure = useDisclosure();
	const cancelDelRef = useRef<HTMLButtonElement>(null);
	const [delBusy, setDelBusy] = useState(false);

	const [resetResult, setResetResult] = useState<{ email: string; password: string } | null>(null);
	const resetDisclosure = useDisclosure();
	const [resetBusyUid, setResetBusyUid] = useState<string | null>(null);

	function askDelete(u: StaffMember) {
		setDelTarget(u);
		delDisclosure.onOpen();
	}

	async function confirmDelete() {
		if (!delTarget) return;
		setDelBusy(true);
		try {
			const functions = getFunctions(app);
			const fn = httpsCallable(functions, "deleteStaff");
			await fn({ uid: delTarget.uid });
			toast({
				title: t("delete_success"),
				description: delTarget.email || delTarget.uid,
				status: "success",
				duration: 4000,
				isClosable: true,
				position: "top",
			});
			delDisclosure.onClose();
			setDelTarget(null);
			loadStaff();
		} catch (e: any) {
			toast({
				title: t("delete_failed"),
				description: e?.message,
				status: "error",
				duration: 6000,
				isClosable: true,
				position: "top",
			});
		} finally {
			setDelBusy(false);
		}
	}

	async function doResetPassword(u: StaffMember) {
		setResetBusyUid(u.uid);
		try {
			const functions = getFunctions(app);
			const fn = httpsCallable<{ uid: string }, { tempPassword: string }>(functions, "resetStaffPassword");
			const res = await fn({ uid: u.uid });
			setResetResult({ email: u.email || u.uid, password: res.data.tempPassword });
			resetDisclosure.onOpen();
		} catch (e: any) {
			toast({
				title: t("reset_failed"),
				description: e?.message,
				status: "error",
				duration: 6000,
				isClosable: true,
				position: "top",
			});
		} finally {
			setResetBusyUid(null);
		}
	}

	async function copyToClipboard(text: string) {
		try {
			await navigator.clipboard.writeText(text);
			toast({ title: t("reset_copied"), status: "success", duration: 2000, position: "top" });
		} catch {
			toast({ title: t("reset_copy"), status: "warning", duration: 2000, position: "top" });
		}
	}

	function fmtDate(iso: string | null): string {
		if (!iso) return t("team_never");
		const d = new Date(iso);
		if (isNaN(d.getTime())) return t("team_never");
		return new Intl.DateTimeFormat(lang === "es" ? "es" : lang === "fr" ? "fr" : "en", {
			year: "numeric", month: "short", day: "numeric",
		}).format(d);
	}

	// --- Add staff member ---
	const [addEmail, setAddEmail] = useState("");
	const [addPw, setAddPw] = useState("");
	const [addPwConfirm, setAddPwConfirm] = useState("");
	const [addBusy, setAddBusy] = useState(false);
	const [addErr, setAddErr] = useState<string | null>(null);
	const addCanSubmit =
		addEmail.length > 5 && addPw.length >= 6 && addPw === addPwConfirm && !addBusy;

	async function doAddUser(e: React.FormEvent) {
		e.preventDefault();
		setAddErr(null);
		setAddBusy(true);
		try {
			const auth = getAuth();
			await createUserWithEmailAndPassword(auth, addEmail, addPw);
			setAddEmail(""); setAddPw(""); setAddPwConfirm("");
			toast({
				title: t("user_added"),
				description: addEmail,
				status: "success",
				duration: 4000,
				isClosable: true,
				position: "top",
			});
		} catch (err: any) {
			const code = err?.code || "";
			if (code.includes("email-already-in-use")) setAddErr(t("email_in_use"));
			else if (code.includes("weak-password")) setAddErr(t("password_weak"));
			else setAddErr(err?.message || t("user_add_failed"));
		} finally {
			setAddBusy(false);
			loadStaff();
		}
	}

	const userInitial = (currentUser?.displayName || currentUser?.email || "?")
		.slice(0, 1)
		.toUpperCase();

	return (
		<TemplatePage sidebar>
			<Box w="100%" bgGradient={`linear(to-b, ${C.bg} 0%, white 200px)`} borderRadius="2xl" p={{ base: 4, md: 6 }}>
				<VStack spacing={6} align="stretch" w="100%">

					{/* Header */}
					<Box>
						<Text fontSize="xs" color={C.primary} fontWeight="700" textTransform="uppercase" letterSpacing="0.18em" mb={1}>
							SalveMonos · MonoSOS
						</Text>
						<Heading as="h1" fontSize={{ base: "3xl", md: "4xl" }} fontWeight="800" letterSpacing="-0.025em" color={C.ink} lineHeight="1.05">
							{emp_account.header[lang]}
						</Heading>
						<Text color={C.muted} fontSize="sm" mt={2}>
							{emp_account.description[lang]}
						</Text>
					</Box>

					{/* Profile + session */}
					<Section icon={<BiUser />} title={t("section_profile")}>
						<Stack direction={{ base: "column", sm: "row" }} spacing={5} align={{ base: "stretch", sm: "center" }}>
							<Avatar
								size="lg"
								name={userInitial}
								bg={C.primary}
								color="white"
								fontWeight="700"
							/>
							<Box flex="1">
								<Text fontSize="xs" color={C.muted} fontWeight="700" textTransform="uppercase" letterSpacing="0.08em">
									{t("signed_in_as")}
								</Text>
								<Text fontSize="lg" fontWeight="700" color={C.ink} mt={0.5}>
									{currentUser?.email || "—"}
								</Text>
								{currentUser?.uid && (
									<Text fontSize="xs" color={C.muted} mt={1} fontFamily="mono">
										{t("uid_label")}: {currentUser.uid}
									</Text>
								)}
							</Box>
							<Button
								leftIcon={<BiLogOut />}
								onClick={logout}
								bg="white"
								color={C.ink}
								borderWidth="1px"
								borderColor={C.border}
								_hover={{ bg: "#FEF2F2", borderColor: "#FCA5A5", color: "#B91C1C" }}
								borderRadius="lg"
								fontWeight="600"
							>
								{login_t.logout[lang]}
							</Button>
						</Stack>
					</Section>

					{/* Change password */}
					<Section icon={<BiKey />} title={t("section_password")} subtitle={t("password_min")}>
						<form onSubmit={doChangePassword}>
							<VStack spacing={4} align="stretch" maxW="md">
								<FormControl>
									<FormLabel fontSize="sm" fontWeight="600" color={C.ink}>
										{t("current_password")}
									</FormLabel>
									<PasswordInput value={curPw} onChange={setCurPw} autoComplete="current-password" />
								</FormControl>

								<FormControl isInvalid={pwTooShort}>
									<FormLabel fontSize="sm" fontWeight="600" color={C.ink}>
										{t("new_password")}
									</FormLabel>
									<PasswordInput value={newPw} onChange={setNewPw} autoComplete="new-password" />
									{pwTooShort && (
										<Text color="red.500" fontSize="xs" mt={1}>
											{t("password_min")}
										</Text>
									)}
								</FormControl>

								<FormControl isInvalid={pwMismatch}>
									<FormLabel fontSize="sm" fontWeight="600" color={C.ink}>
										{t("confirm_new_password")}
									</FormLabel>
									<PasswordInput value={confirmPw} onChange={setConfirmPw} autoComplete="new-password" />
									{pwMismatch && (
										<Text color="red.500" fontSize="xs" mt={1}>
											{emp_account.error_pass[lang]}
										</Text>
									)}
								</FormControl>

								{pwErr && (
									<Alert status="error" borderRadius="lg" fontSize="sm">
										<AlertIcon />
										{pwErr}
									</Alert>
								)}

								<Box>
									<Button
										type="submit"
										isDisabled={!pwCanSubmit}
										isLoading={pwBusy}
										loadingText={t("saving")}
										bg={C.primary}
										color="white"
										_hover={{ bg: C.primaryDark }}
										borderRadius="lg"
										px={6}
										fontWeight="600"
									>
										{t("save_password")}
									</Button>
								</Box>
							</VStack>
						</form>
					</Section>

					{/* Staff team */}
					<Section icon={<BiGroup />} title={t("section_team")} subtitle={t("section_team_help")}>
						{staffLoading && (
							<HStack color={C.muted}>
								<Spinner size="sm" />
								<Text fontSize="sm">{t("team_loading")}</Text>
							</HStack>
						)}
						{staffErr && (
							<Alert status="error" borderRadius="lg" fontSize="sm">
								<AlertIcon />
								{staffErr}
							</Alert>
						)}
						{!staffLoading && !staffErr && staff && staff.length === 0 && (
							<Text color={C.muted} fontSize="sm">{t("team_empty")}</Text>
						)}
						{!staffLoading && staff && staff.length > 0 && (
							<>
								<HStack mb={3}>
									<Text fontSize="sm" color={C.muted}>
										<Text as="span" fontWeight="700" color={C.ink}>{staff.length}</Text>{" "}
										{t("team_count")}
									</Text>
								</HStack>
								<Box borderWidth="1px" borderColor={C.border} borderRadius="lg" overflow="hidden">
									{staff.map((u, i) => {
										const isYou = u.uid === currentUser?.uid;
										const name = u.displayName || u.email?.split("@")[0] || u.uid.slice(0, 6);
										return (
											<HStack
												key={u.uid}
												spacing={3}
												px={{ base: 3, sm: 4 }}
												py={3}
												bg={isYou ? C.bg : "white"}
												borderTopWidth={i === 0 ? 0 : 1}
												borderColor={C.border}
												align="flex-start"
											>
												<Avatar
													size="sm"
													name={name.slice(0, 1).toUpperCase()}
													bg={isYou ? C.primary : "#94A3B8"}
													color="white"
													fontWeight="700"
													flexShrink={0}
													mt={1}
												/>
												<Box flex="1" minW={0}>
													<HStack spacing={2} flexWrap="wrap">
														<Text fontWeight="600" color={C.ink} fontSize="sm" isTruncated maxW={{ base: "190px", sm: "320px", md: "100%" }}>
															{u.email || u.uid}
														</Text>
														{isYou && (
															<Box
																bg={C.primaryLight}
																color={C.primary}
																fontSize="2xs"
																fontWeight="700"
																textTransform="uppercase"
																px={2}
																py={0.5}
																borderRadius="full"
															>
																{t("team_you")}
															</Box>
														)}
														{u.disabled && (
															<Box bg="#FEE2E2" color="#DC2626" fontSize="2xs" fontWeight="700" px={2} py={0.5} borderRadius="full">
																disabled
															</Box>
														)}
													</HStack>
													<Stack
														direction={{ base: "column", sm: "row" }}
														spacing={{ base: 0, sm: 4 }}
														mt={0.5}
													>
														<Text fontSize="xs" color={C.muted}>
															{t("team_joined")}: {fmtDate(u.creationTime)}
														</Text>
														<Text fontSize="xs" color={C.muted}>
															{t("team_last_signin")}: {fmtDate(u.lastSignInTime)}
														</Text>
													</Stack>
												</Box>
												{!isYou && (
													<HStack spacing={1} flexShrink={0}>
														<Tooltip label={t("team_reset")} hasArrow openDelay={400}>
															<IconButton
																aria-label={t("team_reset")}
																icon={<BiKey />}
																size="sm"
																variant="ghost"
																color={C.muted}
																_hover={{ bg: C.primaryLight, color: C.primary }}
																isLoading={resetBusyUid === u.uid}
																onClick={() => doResetPassword(u)}
																minW="40px"
																minH="40px"
															/>
														</Tooltip>
														<Tooltip label={t("team_delete")} hasArrow openDelay={400}>
															<IconButton
																aria-label={t("team_delete")}
																icon={<BiTrash />}
																size="sm"
																variant="ghost"
																color={C.muted}
																_hover={{ bg: "#FEE2E2", color: "#DC2626" }}
																onClick={() => askDelete(u)}
																minW="40px"
																minH="40px"
															/>
														</Tooltip>
													</HStack>
												)}
											</HStack>
										);
									})}
								</Box>
							</>
						)}
					</Section>

					{/* Add user */}
					<Section icon={<BiUserPlus />} title={t("section_add_user")}>
						<form onSubmit={doAddUser}>
							<VStack spacing={4} align="stretch" maxW="md">
								<FormControl>
									<FormLabel fontSize="sm" fontWeight="600" color={C.ink}>
										{t("email_label")}
									</FormLabel>
									<Input
										type="email"
										value={addEmail}
										onChange={(e) => setAddEmail(e.target.value)}
										bg="white"
										borderColor={C.border}
										focusBorderColor={C.primary}
										borderRadius="lg"
										placeholder="staff@example.org"
										autoComplete="off"
									/>
								</FormControl>

								<FormControl>
									<FormLabel fontSize="sm" fontWeight="600" color={C.ink}>
										{t("new_password")}
									</FormLabel>
									<PasswordInput value={addPw} onChange={setAddPw} autoComplete="new-password" />
								</FormControl>

								<FormControl isInvalid={addPwConfirm.length > 0 && addPw !== addPwConfirm}>
									<FormLabel fontSize="sm" fontWeight="600" color={C.ink}>
										{t("confirm_new_password")}
									</FormLabel>
									<PasswordInput value={addPwConfirm} onChange={setAddPwConfirm} autoComplete="new-password" />
								</FormControl>

								{addErr && (
									<Alert status="error" borderRadius="lg" fontSize="sm">
										<AlertIcon />
										{addErr}
									</Alert>
								)}

								<Box>
									<Button
										type="submit"
										isDisabled={!addCanSubmit}
										isLoading={addBusy}
										loadingText={t("saving")}
										leftIcon={<BiUserPlus />}
										bg={C.primary}
										color="white"
										_hover={{ bg: C.primaryDark }}
										borderRadius="lg"
										px={6}
										fontWeight="600"
									>
										{login_t.add_user[lang]}
									</Button>
								</Box>
							</VStack>
						</form>
					</Section>

					<Divider borderColor={C.border} />
					<Text fontSize="xs" color={C.muted} textAlign="center">
						MonoSOS · {new Date().getFullYear()}
					</Text>
				</VStack>
			</Box>

			{/* Delete confirmation */}
			<AlertDialog
				isOpen={delDisclosure.isOpen}
				leastDestructiveRef={cancelDelRef}
				onClose={delDisclosure.onClose}
				motionPreset="slideInBottom"
			>
				<AlertDialogOverlay>
					<AlertDialogContent borderRadius="xl">
						<AlertDialogHeader fontSize="lg" fontWeight="700" color={C.ink}>
							{t("delete_confirm_title")}
						</AlertDialogHeader>
						<AlertDialogBody>
							<Text fontSize="sm" color={C.muted}>{t("delete_confirm_body")}</Text>
							{delTarget && (
								<Text mt={3} fontWeight="700" color={C.ink}>
									{delTarget.email || delTarget.uid}
								</Text>
							)}
						</AlertDialogBody>
						<AlertDialogFooter>
							<Button ref={cancelDelRef} onClick={delDisclosure.onClose} variant="ghost" mr={3}>
								{t("delete_confirm_cancel")}
							</Button>
							<Button
								onClick={confirmDelete}
								bg="#DC2626" color="white"
								_hover={{ bg: "#B91C1C" }}
								isLoading={delBusy}
							>
								{t("delete_confirm_yes")}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>

			{/* Reset password — temp password modal */}
			<Modal isOpen={resetDisclosure.isOpen} onClose={() => { resetDisclosure.onClose(); setResetResult(null); }} motionPreset="slideInBottom">
				<ModalOverlay />
				<ModalContent borderRadius="xl">
					<ModalHeader color={C.ink}>{t("reset_modal_title")}</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Text fontSize="sm" color={C.muted} mb={3}>
							{t("reset_modal_body")}
						</Text>
						{resetResult && (
							<Box>
								<Text fontSize="xs" color={C.muted} mb={1}>{t("email_label")}</Text>
								<Text fontWeight="700" color={C.ink} fontSize="sm" mb={3}>{resetResult.email}</Text>
								<Text fontSize="xs" color={C.muted} mb={1}>{t("new_password")}</Text>
								<HStack
									bg={C.bg}
									borderWidth="1px"
									borderColor={C.border}
									borderRadius="lg"
									px={3}
									py={2}
								>
									<Text
										flex="1"
										fontFamily="mono"
										fontSize="md"
										color={C.ink}
										letterSpacing="0.04em"
									>
										{resetResult.password}
									</Text>
									<IconButton
										aria-label={t("reset_copy")}
										icon={<BiCopy />}
										size="sm"
										variant="ghost"
										onClick={() => copyToClipboard(resetResult.password)}
									/>
								</HStack>
							</Box>
						)}
					</ModalBody>
					<ModalFooter>
						<Button onClick={() => { resetDisclosure.onClose(); setResetResult(null); }} bg={C.primary} color="white" _hover={{ bg: C.primaryDark }}>
							{t("reset_close")}
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</TemplatePage>
	);
};

export default EmployeeAccountPage;
